import React, { useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, Coins, AlertCircle, Sparkles, Search, ChevronRight, Activity, PieChart as PieChartIcon, BarChart3, ShieldAlert, AlertTriangle, ArrowUpRight, Zap, Calendar } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend } from 'recharts';
import { MiniSparkline } from '../components/charts/MiniSparkline';
import { useScreenInit } from '../useScreenInit.js';
// --- MOCK DATA ---
const generateDailyCosts = () => {
  const data = [];
  const today = new Date();
  for (let i = 30; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    data.push({
      date: date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: 'short'
      }),
      OpenAI: Math.floor(Math.random() * 50) + 80,
      Anthropic: Math.floor(Math.random() * 30) + 40,
      Google: Math.floor(Math.random() * 20) + 15
    });
  }
  return data;
};
const DAILY_COSTS = generateDailyCosts();
const SERVICES_DATA = [{
  id: 's1',
  name: 'Customer Assistant',
  provider: 'OpenAI',
  model: 'GPT-5.4',
  requests: '124.5K',
  tokens: '45.2M',
  cost: 1245.5,
  trend: [40, 45, 55, 60, 75, 85, 90],
  status: 'warning'
}, {
  id: 's2',
  name: 'Smart Matcher',
  provider: 'Anthropic',
  model: 'Claude Sonnet 4.6',
  requests: '85.2K',
  tokens: '28.4M',
  cost: 840.2,
  trend: [60, 58, 55, 50, 48, 45, 42],
  status: 'success'
}, {
  id: 's3',
  name: 'Content Moderator',
  provider: 'OpenAI',
  model: 'GPT-5.4 mini',
  requests: '210.8K',
  tokens: '15.1M',
  cost: 320.8,
  trend: [20, 22, 25, 24, 26, 28, 30],
  status: 'success'
}, {
  id: 's4',
  name: 'AI Tutor',
  provider: 'Anthropic',
  model: 'Claude Haiku 4.5',
  requests: '45.1K',
  tokens: '12.5M',
  cost: 180.4,
  trend: [15, 18, 16, 20, 19, 22, 25],
  status: 'success'
}, {
  id: 's5',
  name: 'Resume Screener',
  provider: 'OpenAI',
  model: 'GPT-5.4',
  requests: '12.4K',
  tokens: '8.2M',
  cost: 650.0,
  trend: [80, 85, 90, 95, 100, 110, 120],
  status: 'danger'
}, {
  id: 's6',
  name: 'Document Analyzer',
  provider: 'Google',
  model: 'Gemini 3.1 Pro',
  requests: '32.6K',
  tokens: '18.4M',
  cost: 420.6,
  trend: [30, 32, 35, 34, 36, 38, 40],
  status: 'success'
}, {
  id: 's7',
  name: 'Chatbot HR',
  provider: 'OpenAI',
  model: 'GPT-5.4 mini',
  requests: '56.2K',
  tokens: '5.8M',
  cost: 110.2,
  trend: [10, 12, 11, 10, 12, 14, 15],
  status: 'success'
}, {
  id: 's8',
  name: 'Email Classifier',
  provider: 'Google',
  model: 'Gemini 3.1 Flash-Lite',
  requests: '180.5K',
  tokens: '9.2M',
  cost: 85.5,
  trend: [5, 6, 5, 7, 6, 8, 7],
  status: 'success'
}].sort((a, b) => b.cost - a.cost);
const PROVIDER_DATA = [{
  name: 'OpenAI',
  value: 58,
  color: '#00B4D8',
  cost: 2326.5,
  models: 3,
  topModel: 'GPT-5.4',
  trend: '+15%'
}, {
  name: 'Anthropic',
  value: 28,
  color: '#2A3B5C',
  cost: 1020.6,
  models: 2,
  topModel: 'Claude Sonnet 4.6',
  trend: '-5%'
}, {
  name: 'Google',
  value: 14,
  color: '#10b981',
  cost: 506.1,
  models: 2,
  topModel: 'Gemini 3.1 Pro',
  trend: '+2%'
}];
const TOKEN_COST_DATA = [{
  name: 'GPT-5.4',
  cost: 30.0,
  fill: '#00B4D8'
}, {
  name: 'Claude Sonnet 4.6',
  cost: 15.0,
  fill: '#2A3B5C'
}, {
  name: 'Gemini 3.1 Pro',
  cost: 7.0,
  fill: '#10b981'
}, {
  name: 'GPT-5.4',
  cost: 5.0,
  fill: '#00B4D8'
}, {
  name: 'Claude Haiku 4.5',
  cost: 0.25,
  fill: '#2A3B5C'
}, {
  name: 'GPT-5.4 mini',
  cost: 0.15,
  fill: '#00B4D8'
}, {
  name: 'Gemini 3.1 Flash-Lite',
  cost: 0.07,
  fill: '#10b981'
}].sort((a, b) => b.cost - a.cost);
const BUDGET_DATA = [{
  id: 'b1',
  name: 'Customer Support',
  allocated: 2500,
  consumed: 2125,
  trend: '+12%'
}, {
  id: 'b2',
  name: 'Content Generation',
  allocated: 1000,
  consumed: 920,
  trend: '+25%'
}, {
  id: 'b3',
  name: 'HR Automation',
  allocated: 1500,
  consumed: 675,
  trend: '-5%'
}, {
  id: 'b4',
  name: 'Internal Tools',
  allocated: 2000,
  consumed: 600,
  trend: '+2%'
} // 30%
];
// --- COMPONENTS ---
export function CostManagementDetailPage() {
  const screenInit = useScreenInit();
  const [activeTab, setActiveTab] = useState(screenInit.activeTab ?? 'Panoramica');
  const [period, setPeriod] = useState('30G');
  const [searchQuery, setSearchQuery] = useState('');
  const filteredServices = useMemo(() => {
    return SERVICES_DATA.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()) || s.provider.toLowerCase().includes(searchQuery.toLowerCase()) || s.model.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [searchQuery]);
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(value);
  };
  return <div className="min-h-screen bg-[#F4F6F8] p-4 md:p-8" data-id="element-2220" data-mp="1lk240ilkclna">
      {/* 1. HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8" data-id="element-2221" data-mp="1qgsjb3pjnd7n">
        <div data-id="element-2222" data-mp="1vfdozinkep3h">
          <h1 className="text-[28px] md:text-[32px] font-semibold text-[#1A202C] tracking-tight" data-id="element-2223" data-mp="1veu4bu6j5mlr">
            Cost Management
          </h1>
        </div>
        <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1 shadow-sm" data-id="element-2224" data-mp="24hmv7wbsvwb2">
          {['7G', '30G', '90G', '12M'].map(p => <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors ${period === p ? 'bg-[#F4F6F8] text-[#2A3B5C] shadow-sm' : 'text-gray-500 hover:text-[#1A202C]'}`} data-id="element-2225" data-mp="63to6zurr565">
              {p}
            </button>)}
        </div>
      </div>

      {/* 2. KPI CARDS ROW */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-2226" data-mp="1mctkrfu3j545">
        {/* Card 1 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-2227" data-mp="1ifp3viexngl0">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-2228" data-mp="1war69vkkmnt2">
            Spesa Totale Mese
          </h3>
          <div className="flex items-baseline gap-2 mt-auto" data-id="element-2229" data-mp="262x6ttow2djv">
            <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-2230" data-mp="2623yioaa7fzm">€4.832</span>
          </div>
        </div>

        {/* Card 2 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-2231" data-mp="2f3lbvywb13km">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-2232" data-mp="2a4ii5alryjry">
            Costo Medio/Giorno
          </h3>
          <div className="flex items-baseline gap-2 mt-auto" data-id="element-2233" data-mp="2tqk3btv5vxzc">
            <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-2234" data-mp="2to7kzflhrllr">€161</span>
          </div>
        </div>

        {/* Card 3 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-2235" data-mp="2kpvwpk83kzwh">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-2236" data-mp="2oqxmzbk1e1mb">
            Forecast Fine Mese
          </h3>
          <div className="flex items-baseline gap-2 mt-auto" data-id="element-2237" data-mp="2azj3z4q4q0f9">
            <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-2238" data-mp="2axzld7h03dab">€5.420</span>
          </div>
        </div>

        {/* Card 4 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-2239" data-mp="2124y1z1u0u17">
          <div className="flex justify-between items-center mb-3" data-id="element-2240" data-mp="2606j2pitj3fb">
            <h3 className="text-[13px] font-medium text-gray-500" data-id="element-2241" data-mp="25yd8iz533z21">
              Budget Rimanente
            </h3>
            <span className="text-[11px] font-medium text-gray-400" data-id="element-2242" data-mp="1wzrvfjidtc8n">
              €7.000 tot
            </span>
          </div>
          <div className="mt-auto" data-id="element-2243" data-mp="2cgzwk4u6y4ip">
            <div className="flex items-baseline gap-1 mb-2" data-id="element-2244" data-mp="2ch9kznzl83ma">
              <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-2245" data-mp="27jzqhtxcd1rl">
                €2.168
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5" data-id="element-2246" data-mp="22i208fqix6p5">
              <div className="bg-amber-500 h-1.5 rounded-full" style={{
              width: '69%'
            }} data-id="element-2247" data-mp="1yh8cbmd2zrw9"></div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. TABS NAVIGATION */}
      <div className="border-b-2 border-gray-300 mb-6" data-id="element-2248" data-mp="1jt42l7w0aqpm">
        <div className="-mb-[2px] flex items-center gap-8 overflow-x-auto custom-scrollbar" data-id="element-2249" data-mp="1jtx3vtc74o0d">
          {['Panoramica', 'Per Servizio', 'Per Provider', 'Budget'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 text-[15px] font-medium transition-colors whitespace-nowrap border-b-2 ${activeTab === tab ? 'border-[#00B4D8] text-[#2A3B5C]' : 'border-transparent text-gray-500 hover:text-gray-800'}`} data-id="element-2250" data-mp="1as5wmk20c94e">
                {tab}
              </button>)}
        </div>
      </div>

      {/* 4. TAB CONTENT */}

      {/* TAB 1: PANORAMICA */}
      {activeTab === 'Panoramica' && <div className="space-y-6" data-id="element-2251" data-mp="3vecxkxltbf9n">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2252" data-mp="2yr9dm0vmdt2u">
            {/* Area Chart */}
            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-id="element-2253" data-mp="32ra3qis2zspq">
              <div className="flex justify-between items-center mb-6" data-id="element-2254" data-mp="37qwe8kgiyuuu">
                <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-2255" data-mp="3chv1k3zxkt7l">
                  Trend Costi Giornalieri
                </h2>
                <div className="flex items-center gap-4 text-[11px] font-medium" data-id="element-2256" data-mp="2f1ftm8z5ci96">
                  <div className="flex items-center gap-1.5" data-id="element-2257" data-mp="2k0scyjqt7770">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#00B4D8]" data-id="element-2258" data-mp="2jz7jkxg8duaq"></div>
                    OpenAI
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-2259" data-mp="3jpw8s628nf7v">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#2A3B5C]" data-id="element-2260" data-mp="3jsa48g0tytfy"></div>
                    Anthropic
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-2261" data-mp="3tp4zmq1t4dq0">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#10b981]" data-id="element-2262" data-mp="3tmirao73du6q"></div>
                    Google
                  </div>
                </div>
              </div>
              <div className="h-[360px] w-full" data-id="element-2263" data-mp="310hagjdxt476">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2264" data-mp="35zaqojklz9b1">
                  <AreaChart data={DAILY_COSTS} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-2265" data-mp="35xprggncn26u">
                    <defs data-id="element-2266" data-mp="2i85a226ugczo">
                      <linearGradient id="colorOpenAI" x1="0" y1="0" x2="0" y2="1" data-id="element-2267" data-mp="2i7ddop5fab8c">
                        <stop offset="5%" stopColor="#00B4D8" stopOpacity={0.3} data-id="element-2268" data-mp="20rhkmagbtriu" />
                        <stop offset="95%" stopColor="#00B4D8" stopOpacity={0} data-id="element-2269" data-mp="1vv0vbcxe5746" />
                      </linearGradient>
                      <linearGradient id="colorAnthropic" x1="0" y1="0" x2="0" y2="1" data-id="element-2270" data-mp="1i10557ki8sff">
                        <stop offset="5%" stopColor="#2A3B5C" stopOpacity={0.3} data-id="element-2271" data-mp="1820yfujav6v2" />
                        <stop offset="95%" stopColor="#2A3B5C" stopOpacity={0} data-id="element-2272" data-mp="tg3td06wsldd" />
                      </linearGradient>
                      <linearGradient id="colorGoogle" x1="0" y1="0" x2="0" y2="1" data-id="element-2273" data-mp="kh08prs3fxwr">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} data-id="element-2274" data-mp="1d6gysis8r2pt" />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} data-id="element-2275" data-mp="13eax2hc6t6xf" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-2276" data-mp="20tovicblt3g8" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#6B7280'
                }} dy={10} minTickGap={30} data-id="element-2277" data-mp="2a6e711z9pubi" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#6B7280'
                }} tickFormatter={val => `€${val}`} data-id="element-2278" data-mp="1raejc3dbpcxq" />
                    <Tooltip contentStyle={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                  fontSize: '12px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }} itemStyle={{
                  color: '#1A202C',
                  fontWeight: 500
                }} formatter={(value: number, name: string) => [`€${value}`, name]} data-id="element-2279" data-mp="1wb2bwth7a1u2" />
                    <Area type="monotone" dataKey="Google" stackId="1" stroke="#10b981" fill="url(#colorGoogle)" strokeWidth={2} data-id="element-2280" data-mp="205dfxb1zagsn" />
                    <Area type="monotone" dataKey="Anthropic" stackId="1" stroke="#2A3B5C" fill="url(#colorAnthropic)" strokeWidth={2} data-id="element-2281" data-mp="2etrgtgfu3v6k" />
                    <Area type="monotone" dataKey="OpenAI" stackId="1" stroke="#00B4D8" fill="url(#colorOpenAI)" strokeWidth={2} data-id="element-2282" data-mp="c8dyy1359bp7" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Top Services */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex flex-col" data-id="element-2283" data-mp="uxc03iwve9th">
              <div className="flex justify-between items-center mb-6" data-id="element-2284" data-mp="qxjxrny2kzwk">
                <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-2285" data-mp="m5iehi2hi8x1">
                  Top Servizi per Costo
                </h2>
                <button className="text-[12px] text-[#00B4D8] font-medium hover:underline" data-id="element-2286" data-mp="h6p809lm7i6d">
                  Vedi tutti
                </button>
              </div>
              <div className="flex-1 flex flex-col gap-4" data-id="element-2287" data-mp="19vmelfggrqja">
                {SERVICES_DATA.slice(0, 5).map((service, idx) => {
              const totalCost = SERVICES_DATA.reduce((acc, curr) => acc + curr.cost, 0);
              const percent = (service.cost / totalCost * 100).toFixed(1);
              return <div key={service.id} className="flex items-center justify-between group" data-id="element-2288" data-mp="1o0wgewntscdc" data-mpi={idx}>
                      <div className="flex items-center gap-3 overflow-hidden" data-id="element-2289" data-mp="1a3q0d9uhkrrt">
                        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500 shrink-0" data-id="element-2290" data-mp="1f21dbtg2vie9">
                          {idx + 1}
                        </div>
                        <div className="min-w-0" data-id="element-2291" data-mp="154dht1ane6v1">
                          <p className="text-[13px] font-medium text-[#1A202C] truncate" data-id="element-2292" data-mp="2vg19qufpspwr">
                            {service.name}
                          </p>
                          <p className="text-[11px] text-gray-500 truncate" data-id="element-2293" data-mp="nt7y8yms6rle">
                            {service.model}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 shrink-0 pl-2" data-id="element-2294" data-mp="xirhw6ibiphb">
                        <div className="w-16 hidden sm:block" data-id="element-2295" data-mp="xkvvxh9xl4vb">
                          <MiniSparkline data={service.trend} color={service.status === 'danger' ? '#ef4444' : '#00B4D8'} className="h-[16px]" data-id="element-2296" data-mp="sqrlup5dcgq0" />
                        </div>
                        <div className="text-right" data-id="element-2297" data-mp="1bjc319bpgxdz">
                          <p className="text-[13px] font-semibold text-[#1A202C]" data-id="element-2298" data-mp="3i8qmq742gifp">
                            €{service.cost.toFixed(0)}
                          </p>
                          <p className="text-[10px] text-gray-400" data-id="element-2299" data-mp="3dcjo6ze0297s">
                            {percent}%
                          </p>
                        </div>
                      </div>
                    </div>;
            })}
              </div>
            </div>
          </div>

          {/* Anomalies & Suggestions */}
          <h2 className="text-[16px] font-semibold text-[#1A202C] mt-8 mb-4" data-id="element-2300" data-mp="364uoywxldgwz">
            Anomalie & Suggerimenti
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2301" data-mp="29hgc19r5c23p">
            <div className="bg-red-50 border border-red-100 rounded-xl p-5 flex items-start gap-4" data-id="element-2302" data-mp="2di9z60wrlcvj">
              <div className="p-2 bg-red-100 rounded-lg shrink-0 mt-0.5" data-id="element-2303" data-mp="2ifa4uappcdm4">
                <AlertTriangle className="w-5 h-5 text-red-600" data-id="element-2304" data-mp="2nf4te7m08r40" />
              </div>
              <div data-id="element-2305" data-mp="3tvgwmn3ooz6g">
                <div className="flex items-center gap-2 mb-1" data-id="element-2306" data-mp="3tw05lebcwd8c">
                  <h3 className="text-[14px] font-semibold text-red-900" data-id="element-2307" data-mp="3txuqwtqji7cy">
                    Picco di spesa rilevato
                  </h3>
                  <span className="bg-red-100 text-red-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide" data-id="element-2308" data-mp="1n7hn61y5c24z">
                    Azione richiesta
                  </span>
                </div>
                <p className="text-[13px] text-red-800/80 leading-relaxed mb-3" data-id="element-2309" data-mp="1dgdbpc7oizew">
                  Il servizio <strong data-id="element-2310" data-mp="18hagejhi47k4">Resume Screener</strong> ha registrato un
                  aumento dei costi del 42% negli ultimi 3 giorni, superando la
                  soglia di allerta.
                </p>
                <button className="text-[12px] font-medium text-red-700 hover:text-red-800 flex items-center gap-1" data-id="element-2311" data-mp="zgm35qzxmb8a">
                  Analizza traffico <ArrowUpRight className="w-3.5 h-3.5" data-id="element-2312" data-mp="ukn6lg4g9mws" />
                </button>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 flex items-start gap-4" data-id="element-2313" data-mp="2owldhbfae6pi">
              <div className="p-2 bg-blue-100 rounded-lg shrink-0 mt-0.5" data-id="element-2314" data-mp="2jxh4olw9yazp">
                <Sparkles className="w-5 h-5 text-[#00B4D8]" data-id="element-2315" data-mp="2jve4xa5bl3hs" />
              </div>
              <div data-id="element-2316" data-mp="3d7jv4bc259xs">
                <div className="flex items-center gap-2 mb-1" data-id="element-2317" data-mp="3d9we8rtjxqcf">
                  <h3 className="text-[14px] font-semibold text-blue-900" data-id="element-2318" data-mp="38a9zuf1zm421">
                    Suggerimento di ottimizzazione
                  </h3>
                  <span className="bg-blue-100 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide" data-id="element-2319" data-mp="2zgn76xt2tgx2">
                    Risparmio stimato: €340/mese
                  </span>
                </div>
                <p className="text-[13px] text-blue-800/80 leading-relaxed mb-3" data-id="element-2320" data-mp="2pi7ptmjupm0r">
                  Il servizio <strong data-id="element-2321" data-mp="2kimh4kevpzau">Content Moderator</strong> utilizza
                  GPT-5.4. Passando a <strong data-id="element-2322" data-mp="2gi2ljl9yf3qt">GPT-5.4 mini</strong> potresti
                  ridurre i costi dell'85% mantenendo performance simili per
                  questo task.
                </p>
                <button className="text-[12px] font-medium text-[#00B4D8] hover:text-[#0096b4] flex items-center gap-1" data-id="element-2323" data-mp="iporexgefmu4">
                  Applica modifica <ArrowUpRight className="w-3.5 h-3.5" data-id="element-2324" data-mp="1woufao72bae7" />
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* TAB 2: PER SERVIZIO */}
      {activeTab === 'Per Servizio' && <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col" data-id="element-2325" data-mp="26entlwyla3m5">
          <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4" data-id="element-2326" data-mp="2ahk4t2lsuxg2">
            <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-2327" data-mp="1io9yfxkpjonc">
              Dettaglio Costi per Servizio
            </h2>
            <div className="relative w-full sm:w-72" data-id="element-2328" data-mp="1mplr6byvopzr">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-2329" data-mp="1mrzhy8mizg1i" />
              <input type="text" placeholder="Cerca servizio, provider o modello..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-[13px] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8] transition-all" data-id="element-2330" data-mp="1rmvglrm9p1nl" />
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto" data-id="element-2331" data-mp="1cxyvq6ciu7xn">
            <table className="w-full text-left border-collapse" data-id="element-2332" data-mp="18200bysrzpnq">
              <thead data-id="element-2333" data-mp="kb4erjewxqox">
                <tr className="bg-gray-50/50 border-b border-gray-100" data-id="element-2334" data-mp="kam7zsumju2r">
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2335" data-mp="fckr9eko86ug">
                    Servizio
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2336" data-mp="u2iadg3qu6cs">
                    Provider & Modello
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-2337" data-mp="1juvwjj95pko">
                    Richieste
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-2338" data-mp="288n0f8edznog">
                    Token
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-2339" data-mp="2d1q07y9rbtd3">
                    Costo
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-2340" data-mp="23alhcngynocz">
                    Trend (7gg)
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-2341" data-mp="2vxoh1ixdi54t">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-2342" data-mp="2mwq76k0973eb">
                {filteredServices.map(service => <tr key={service.id} className="hover:bg-gray-50/50 transition-colors group cursor-pointer" data-id="element-2343" data-mp="16ihx3owo6mjk">
                    <td className="px-6 py-4" data-id="element-2344" data-mp="1qtkqw362b44u">
                      <div className="flex items-center gap-3" data-id="element-2345" data-mp="1qudskpq5z3g4">
                        <div className={`w-2 h-2 rounded-full ${service.status === 'danger' ? 'bg-red-500' : 'bg-emerald-500'}`} data-id="element-2346" data-mp="1m09ivypikhbc"></div>
                        <span className="text-[13px] font-medium text-[#2a3a54]" data-id="element-2347" data-mp="1d25wl06pfgih">
                          {service.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-2348" data-mp="24tltpo5w0lmy">
                      <div className="flex flex-col" data-id="element-2349" data-mp="20qp61j1hpzc5">
                        <span className="text-[13px] text-[#1A202C] font-medium" data-id="element-2350" data-mp="20pe4zqzfj5oq">
                          {service.provider}
                        </span>
                        <span className="text-[11px] text-gray-500" data-id="element-2351" data-mp="pbac6o4kvnl9">
                          {service.model}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-2352" data-mp="2w02hvgbg7rqm">
                      <span className="text-[13px] text-gray-600" data-id="element-2353" data-mp="2w1nct71odcp0">
                        {service.requests}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-2354" data-mp="3508pwmodnzie">
                      <span className="text-[13px] text-gray-600" data-id="element-2355" data-mp="34ynuyvy5iek0">
                        {service.tokens}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-2356" data-mp="3epikc96p1u9o">
                      <span className="text-[13px] font-semibold text-[#1A202C]" data-id="element-2357" data-mp="3jp4yckucjejj">
                        €{service.cost.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 w-32" data-id="element-2358" data-mp="3sq31z20vhk1l">
                      <MiniSparkline data={service.trend} color={service.status === 'danger' ? '#ef4444' : '#00B4D8'} className="h-[24px]" data-id="element-2359" data-mp="3sorx0yvoy68w" />
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-2360" data-mp="31n7vyli7eses">
                      <button className="text-[12px] font-medium text-[#00B4D8] hover:text-[#0096b4] transition-colors opacity-0 group-hover:opacity-100" data-id="element-2361" data-mp="2woo8ggnyp8kb">
                        Dettagli
                      </button>
                    </td>
                  </tr>)}
                {filteredServices.length === 0 && <tr data-id="element-2362" data-mp="3kk8zs300umh5">
                    <td colSpan={7} className="px-6 py-8 text-center text-[13px] text-gray-500" data-id="element-2363" data-mp="3kjh5qwm2owsz">
                      Nessun servizio trovato per "{searchQuery}"
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden flex flex-col divide-y divide-gray-100" data-id="element-2364" data-mp="77731t43rh1h">
            {filteredServices.map(service => <div key={service.id} className="p-4 flex flex-col gap-3" data-id="element-2365" data-mp="ltmjysfn2pc7">
                <div className="flex justify-between items-start" data-id="element-2366" data-mp="luflnezqqonh">
                  <div className="flex items-center gap-2" data-id="element-2367" data-mp="qro31dd2eono">
                    <div className={`w-2 h-2 rounded-full ${service.status === 'danger' ? 'bg-red-500' : 'bg-emerald-500'}`} data-id="element-2368" data-mp="csy2touy88v3"></div>
                    <div data-id="element-2369" data-mp="gwmccd355eiz">
                      <p className="text-[13px] font-medium text-[#2a3a54]" data-id="element-2370" data-mp="33emoqymet3x">
                        {service.name}
                      </p>
                      <p className="text-[11px] text-gray-500" data-id="element-2371" data-mp="7umujikay6ez">
                        {service.provider} • {service.model}
                      </p>
                    </div>
                  </div>
                  <span className="text-[14px] font-semibold text-[#1A202C]" data-id="element-2372" data-mp="3ua7nhtpi4q7x">
                    €{service.cost.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-1" data-id="element-2373" data-mp="3l99e0vwanqhy">
                  <div className="w-24" data-id="element-2374" data-mp="4ld6g9ahml8f">
                    <MiniSparkline data={service.trend} color={service.status === 'danger' ? '#ef4444' : '#00B4D8'} className="h-[20px]" data-id="element-2375" data-mp="4kbwk857k4sa" />
                  </div>
                  <button className="text-[12px] font-medium text-[#00B4D8]" data-id="element-2376" data-mp="3mpybsnel20w6">
                    Dettagli
                  </button>
                </div>
              </div>)}
            {filteredServices.length === 0 && <div className="p-8 text-center text-[13px] text-gray-500" data-id="element-2377" data-mp="3cz3odfplov95">
                Nessun servizio trovato per "{searchQuery}"
              </div>}
          </div>
        </div>}

      {/* TAB 3: PER PROVIDER */}
      {activeTab === 'Per Provider' && <div className="space-y-6" data-id="element-2378" data-mp="2z0vwvnbc2m5d">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2379" data-mp="2u2c8lg99oy9u">
            {/* Donut Chart */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex flex-col" data-id="element-2380" data-mp="19ka3vjzjmllg">
              <h2 className="text-[16px] font-semibold text-[#1A202C] mb-6" data-id="element-2381" data-mp="1ejcyeahwd9f6">
                Distribuzione Costi
              </h2>
              <div className="h-[320px] w-full relative flex-1" data-id="element-2382" data-mp="1ikq5sp0n1ko7">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2383" data-mp="1nk2pj0w7qbmk">
                  <PieChart data-id="element-2384" data-mp="1nj9sirn230hm">
                    <Pie data={PROVIDER_DATA} cx="50%" cy="50%" innerRadius={70} outerRadius={90} paddingAngle={2} dataKey="value" stroke="none" data-id="element-2385" data-mp="qtse9oa0c64b">
                      {PROVIDER_DATA.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-2386" data-mp="75t5giz0791j" data-mpi={index} />)}
                    </Pie>
                    <Tooltip formatter={(value: number) => [`${value}%`, 'Quota']} contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }} data-id="element-2387" data-mp="c44cklxwz5g2" />
                  </PieChart>
                </ResponsiveContainer>
                {/* Center Text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" data-id="element-2388" data-mp="3e0vv3c2hrdbo">
                  <span className="text-[12px] text-gray-500 font-medium" data-id="element-2389" data-mp="2z822qq24f2pl">
                    Totale
                  </span>
                  <span className="text-[24px] font-bold text-[#1A202C]" data-id="element-2390" data-mp="2q9qeuvsn2j0u">
                    €3.853
                  </span>
                </div>
              </div>
            </div>

            {/* Provider Breakdown Cards */}
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-2391" data-mp="2gcbyew6ptu8j">
              {PROVIDER_DATA.map(provider => <div key={provider.name} className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 flex flex-col" data-id="element-2392" data-mp="2lc6tlbxix5ze">
                  <div className="flex items-center gap-2 mb-4" data-id="element-2393" data-mp="1wqsczjj79cdt">
                    <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: provider.color
              }} data-id="element-2394" data-mp="2bmroz59fjwo8"></div>
                    <h3 className="text-[15px] font-semibold text-[#1A202C]" data-id="element-2395" data-mp="2klv98ckc2i4b">
                      {provider.name}
                    </h3>
                  </div>
                  <div className="mb-4" data-id="element-2396" data-mp="2udix63holh11">
                    <p className="text-[24px] font-bold text-[#1A202C]" data-id="element-2397" data-mp="2ucg7u000bmn8">
                      €{provider.cost.toFixed(2)}
                    </p>
                    <p className={`text-[12px] font-medium mt-1 ${provider.trend.startsWith('+') ? 'text-red-500' : 'text-emerald-500'}`} data-id="element-2398" data-mp="33decmki9ry6v">
                      {provider.trend} vs mese prec.
                    </p>
                  </div>
                  <div className="mt-auto space-y-2 pt-4 border-t border-gray-100" data-id="element-2399" data-mp="36dfuvdctmotr">
                    <div className="flex justify-between items-center" data-id="element-2400" data-mp="3bc99xa7ranw1">
                      <span className="text-[12px] text-gray-500" data-id="element-2401" data-mp="3gcomw0o4ltdh">
                        Modelli attivi
                      </span>
                      <span className="text-[12px] font-medium text-[#1A202C]" data-id="element-2402" data-mp="3l5zva68l6i7i">
                        {provider.models}
                      </span>
                    </div>
                    <div className="flex justify-between items-center" data-id="element-2403" data-mp="3u3a8hx4yshf2">
                      <span className="text-[12px] text-gray-500" data-id="element-2404" data-mp="2zlhq8e8ca9n">
                        Modello top
                      </span>
                      <span className="text-[12px] font-medium text-[#1A202C]" data-id="element-2405" data-mp="7wvds6w4jk6g">
                        {provider.topModel}
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Cost per 1k Tokens Bar Chart */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-id="element-2406" data-mp="qvq9p51ykfuf">
            <div className="flex justify-between items-center mb-6" data-id="element-2407" data-mp="m12fqqo6qt8g">
              <div data-id="element-2408" data-mp="i2d69969pvu3">
                <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-2409" data-mp="i1tmdmx24xdf">
                  Costo per 1M Token (Input + Output mix)
                </h2>
                <p className="text-[12px] text-gray-500 mt-1" data-id="element-2410" data-mp="19t9ge20y1mdo">
                  Confronto prezzi di listino dei modelli utilizzati
                </p>
              </div>
            </div>
            <div className="h-[360px] w-full" data-id="element-2411" data-mp="10rtbg9swecgu">
              <ResponsiveContainer width="100%" height="100%" data-id="element-2412" data-mp="10qi5c3bzcsmk">
                <BarChart data={TOKEN_COST_DATA} layout="vertical" margin={{
              top: 0,
              right: 30,
              left: 40,
              bottom: 0
            }} data-id="element-2413" data-mp="vtrqtkftq539">
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#E5E7EB" data-id="element-2414" data-mp="189zh5mm9qy28" />
                  <XAxis type="number" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#6B7280'
              }} tickFormatter={val => `€${val}`} data-id="element-2415" data-mp="zam82v224bi1" />
                  <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#1A202C',
                fontWeight: 500
              }} width={120} data-id="element-2416" data-mp="1vzk4sitdajhu" />
                  <Tooltip cursor={{
                fill: '#F3F4F6'
              }} contentStyle={{
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }} formatter={(value: number) => [`€${value.toFixed(2)}`, 'Costo / 1M Token']} data-id="element-2417" data-mp="bl1m03alwy4j" />
                  <Bar dataKey="cost" radius={[0, 4, 4, 0]} barSize={24} data-id="element-2418" data-mp="3gg1oph6kwrlk">
                    {TOKEN_COST_DATA.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} data-id="element-2419" data-mp="3q8h72flsfi6z" data-mpi={index} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>}

      {/* TAB 4: BUDGET */}
      {activeTab === 'Budget' && <div className="space-y-6" data-id="element-2420" data-mp="2ssti8vxezchy">
          {/* Alert Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-id="element-2421" data-mp="2sqh3ermydi94">
            <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" data-id="element-2422" data-mp="wnaxpcz6pu30" />
            <div data-id="element-2423" data-mp="11mxefwe81sgk">
              <h3 className="text-[14px] font-semibold text-amber-900" data-id="element-2424" data-mp="11mf7o5txnvue">
                Attenzione ai limiti di budget
              </h3>
              <p className="text-[13px] text-amber-800/80 mt-1" data-id="element-2425" data-mp="1betc56cebgk9">
                Le linee di budget <strong data-id="element-2426" data-mp="dqszqdtqsxyb">Content Generation</strong> e{' '}
                <strong data-id="element-2427" data-mp="dsdv25nvskx8">Customer Support</strong> hanno superato l'80%
                dell'allocazione mensile. Potrebbe essere necessario un
                ribilanciamento.
              </p>
            </div>
          </div>

          {/* Budget Table */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-2428" data-mp="rqu44397unij">
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between" data-id="element-2429" data-mp="1z4fc3w0ldztb">
              <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-2430" data-mp="234y2rmqr0ztx">
                Stato Linee di Budget
              </h2>
              <button className="text-[13px] text-[#00B4D8] font-medium hover:underline" data-id="element-2431" data-mp="3nm8gymlqg4yw">
                Gestisci Allocazioni
              </button>
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto" data-id="element-2432" data-mp="3etn941jn3iou">
              <table className="w-full text-left border-collapse" data-id="element-2433" data-mp="39wn4zw6coq0d">
                <thead data-id="element-2434" data-mp="39uam9gsrqbm9">
                  <tr className="bg-gray-50/50 border-b border-gray-100" data-id="element-2435" data-mp="34xjwg1r5w1nn">
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2436" data-mp="34uo0giyvjn1u">
                      Linea di Budget
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-2437" data-mp="2vwm3cei9heju">
                      Allocato
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-2438" data-mp="u3o5235pttri">
                      Consumato
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-2439" data-mp="3u5o7dozhbty8">
                      Rimanente
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-64" data-id="element-2440" data-mp="3kgeew80q499l">
                      % Utilizzo
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-2441" data-mp="3pcv3t4fqyrnq">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-2442" data-mp="31hiol7rzkf11">
                  {BUDGET_DATA.map(budget => {
                const percent = budget.consumed / budget.allocated * 100;
                const remaining = budget.allocated - budget.consumed;
                let statusColor = 'bg-emerald-500';
                let badgeClass = 'bg-emerald-50 text-emerald-600 border-emerald-100';
                let statusText = 'In linea';
                if (percent >= 90) {
                  statusColor = 'bg-red-500';
                  badgeClass = 'bg-red-50 text-red-600 border-red-100';
                  statusText = 'Critico';
                } else if (percent >= 70) {
                  statusColor = 'bg-amber-500';
                  badgeClass = 'bg-amber-50 text-amber-600 border-amber-100';
                  statusText = 'Attenzione';
                }
                return <tr key={budget.id} className="hover:bg-gray-50/50 transition-colors" data-id="element-2443" data-mp="330ilttbmnpk3">
                        <td className="px-6 py-4" data-id="element-2444" data-mp="3cxx31v5dkidg">
                          <span className="text-[13px] font-medium text-[#2a3a54]" data-id="element-2445" data-mp="1nhny29u15rk7">
                            {budget.name}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-2446" data-mp="1ikxgtj6hopx7">
                          <span className="text-[13px] text-gray-600" data-id="element-2447" data-mp="2jjp3fbot1oak">
                            {formatCurrency(budget.allocated)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-2448" data-mp="2thv45pyvkodu">
                          <span className="text-[13px] font-semibold text-[#1A202C]" data-id="element-2449" data-mp="2oia39a05a5yh">
                            {formatCurrency(budget.consumed)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-2450" data-mp="2xj8b63dpexmc">
                          <span className="text-[13px] text-gray-600" data-id="element-2451" data-mp="2xguh9xurfrgd">
                            {formatCurrency(remaining)}
                          </span>
                        </td>
                        <td className="px-6 py-4" data-id="element-2452" data-mp="3799vql6ulxwi">
                          <div className="flex items-center gap-3" data-id="element-2453" data-mp="3l7hrwqw28v6m">
                            <div className="flex-1 bg-gray-100 rounded-full h-2" data-id="element-2454" data-mp="3h6o3zximbgdq">
                              <div className={`${statusColor} h-2 rounded-full`} style={{
                          width: `${Math.min(percent, 100)}%`
                        }} data-id="element-2455" data-mp="3h4l5ep3egexe"></div>
                            </div>
                            <span className="text-[12px] font-medium text-gray-600 w-9 text-right" data-id="element-2456" data-mp="3vt4oa0t9yc5r">
                              {percent.toFixed(0)}%
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center" data-id="element-2457" data-mp="3lwrtezlzts9f">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${badgeClass}`} data-id="element-2458" data-mp="384bx0hnlkshh">
                            {statusText}
                          </span>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden flex flex-col divide-y divide-gray-100" data-id="element-2459" data-mp="18qbruqwx2pb2">
              {BUDGET_DATA.map(budget => {
            const percent = budget.consumed / budget.allocated * 100;
            let statusColor = 'bg-emerald-500';
            let badgeClass = 'bg-emerald-50 text-emerald-600 border-emerald-100';
            let statusText = 'In linea';
            if (percent >= 90) {
              statusColor = 'bg-red-500';
              badgeClass = 'bg-red-50 text-red-600 border-red-100';
              statusText = 'Critico';
            } else if (percent >= 70) {
              statusColor = 'bg-amber-500';
              badgeClass = 'bg-amber-50 text-amber-600 border-amber-100';
              statusText = 'Attenzione';
            }
            return <div key={budget.id} className="p-4 flex flex-col gap-3" data-id="element-2460" data-mp="27dh2bsck1qe">
                    <div className="flex justify-between items-start" data-id="element-2461" data-mp="3oae4c2zfgl04">
                      <p className="text-[13px] font-medium text-[#2a3a54]" data-id="element-2462" data-mp="3o81el3nfh6ci">
                        {budget.name}
                      </p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-medium border ${badgeClass}`} data-id="element-2463" data-mp="22e5bveh0qgg">
                        {statusText}
                      </span>
                    </div>
                    <div data-id="element-2464" data-mp="ftss8dbi1bss">
                      <div className="flex justify-between text-[11px] text-gray-500 mb-1.5" data-id="element-2465" data-mp="ft18q0v6f4iv">
                        <span data-id="element-2466" data-mp="ku71fyou6rse">
                          {formatCurrency(budget.consumed)} /{' '}
                          {formatCurrency(budget.allocated)}
                        </span>
                        <span className="font-medium text-gray-700" data-id="element-2467" data-mp="2mlacuq6jwae0">
                          {percent.toFixed(0)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5" data-id="element-2468" data-mp="lvb5ttjargiu">
                        <div className={`${statusColor} h-1.5 rounded-full`} style={{
                    width: `${Math.min(percent, 100)}%`
                  }} data-id="element-2469" data-mp="lxfjv4awtvwu"></div>
                      </div>
                    </div>
                  </div>;
          })}
            </div>
          </div>
        </div>}
    </div>;
}